
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import moment from "moment";
import RouteNames from "@/entities/RouteNames";
import PageHeadIcon from "@/components/page/PageHeadIcon.vue";
import PageHeadContainer from "@/components/page/PageHeadContainerCustom.vue";
import UiButton from "@/components/button/ui-button.vue";
import UiButtonMessage from "@/components/button/ui-button-message.vue";
import EditIcon from "@/components/icons/EditIconNew.vue";
import CalendarIcon from "@/components/icons/CalendarIcon.vue";
import LocationIcon from "@/components/icons/LocationIconBlack.vue";
import RouteParams from "@/entities/RouteParams";
import CompleteModal from "@/components/companyProjects/CompleteModal.vue";
import UserIcon from "@/components/icons/UserIcon.vue";
import StarIcon from "@/components/icons/StarIconFilledBlack.vue";
import ContactIcon from "@/components/icons/ContactIconFilledBlack.vue";
import ReopenIconBlack from "@/components/icons/reopen-icon-black.vue";
import ReopenIconWhite from "@/components/icons/reopen-icon-white.vue";
import CompleteIcon from "@/components/icons/Checkmark.vue";
import CardInfoChip from "@/components/card/CardInfoChip.vue";
import CheckMarkBlack from "@/components/icons/CheckMarkBlack.vue";
import { Project, User } from "@/generated/graphql";
import S3Image from "@/components/common/s3-image.vue";
import LocationImage from "@/views/clients/LocationMaps.vue";
import ChatIcon from "@/components/icons/MsgIcon.vue";
import LIST_INVOICE from "@/apollo/invoice/ListInvoice.gql";
import { hslColorFromName } from "@/utils/hslColorFromName";
import { StyleValue } from "vue";

@Component({
  components: {
    PageHeadIcon,
    LocationImage,
    EditIcon,
    CompleteIcon,
    S3Image,
    ChatIcon,
    ContactIcon,
    PageHeadContainer,
    ReopenIconBlack,
    ReopenIconWhite,
    CheckMarkBlack,
    UiButton,
    CompleteModal,
    UiButtonMessage,
    CalendarIcon,
    LocationIcon,
    UserIcon,
    StarIcon,
    CardInfoChip,
  },
  apollo: {
    invoice: {
      query: LIST_INVOICE,
      variables() {
        return this.invoiceVariables;
      },
      update(data) {
        this.resourceCost = data.invoice;
        return data;
      },
    },
  },
})
export default class ProjectHeader extends Vue {
  @Prop({ type: Object, required: true }) readonly project!: Project;
  @Prop({ type: String, required: true }) readonly userRole!: string;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: String, default: "" }) readonly phoneNumber: String;

  statusProcessing: boolean = false;
  dialog: boolean = false;
  resourceCost: any[] = [];
  isHoveredChat: boolean = false;

  get projectCompleted(): boolean {
    return this.project.completed;
  }

 get createdAt(): string {
  const createdAt = this.project.created_at;
  if (!createdAt) {
    return "Date Created: N/A";
  }
  const date = moment.utc(createdAt).format("DD MMM, YYYY");
  return `Date Created: ${date}`;
}
get uniqueSkills() {
  if (!this.project?.project_skills) return [];

  const seen = new Map();
  return this.project.project_skills.filter(item => {
    const skillName = item.skill?.id;
    if (skillName && !seen.has(skillName)) {
      seen.set(skillName, true);
      return true;
    }
    return false;
  });
}

  get isAdmin(): boolean {
    return this.userRole === "ADMIN";
  }
  message(): void {
    this.$router.push({
      name: "inbox",
      query: {
        recipientUserId: this.project.manager.user.id,
      },
    });
  }

  get initial(): string {
    const firstName = this.project.manager.user?.given_name;
    const lastName = this.project.manager.user?.family_name;

    if (!firstName && !lastName) {
      return "";
    }

    const givenName = firstName ? firstName.trimLeft()[0] : "";
    const familyName = lastName ? lastName.trimLeft()[0] : "";
    return `${givenName}${familyName}`;
  }
  get invoiceVariables() {
    return {
      where: {
        status: { _eq: "APPROVED" },
        ...(this.currentUser.role === "ADMIN" && {
          company: {
            id: {
              _eq: this.currentUser.admin.company.id,
            },
          },
          project: {
            id: {
              _eq: this.project.id,
            },
          },
        }),
      },
    };
  }

  get resourceCostDate() {
    return this.resourceCost.reduce((sum, item) => sum + item.total_amount, 0);
  }

  get bgStyle(): StyleValue {
    return {
      background: hslColorFromName(this.initial.trim().toLowerCase() || ""),
    };
  }

  get clientId(): string {
    return this.project.client_id || " n/a";
  }

  closeModal() {
    this.statusProcessing = false;
    this.dialog = false;
  }
  get isContractor(): boolean {
    return this.userRole === "CONTRACTOR";
  }
  get defaultInitialsClasses(): string {
    return "inline-flex h-6 w-6 rounded-full items-center justify-center";
  }

  get defaultImageClasses() {
    return "text-storm-gray text-sm font-medium rounded-full border border-gray-300 bg-white w-6 sm:w-8 h-6 sm:h-8 mr-2";
  }
  get getAvatarClass() {
    return "chipHeight flex-shrink-0 circleWidth rounded-full bg-primary-500 flex items-center text-xs justify-center text-white";
  }

  get detailsPageRoute(): RouteParams<{ id: string }> {
    return {
      name: RouteNames.PROJECT_DETAILS,
      params: {
        id: this.project?.id,
      },
    };
  }

  get managerLink(): RouteParams<{ id: string }> | undefined {
    const managerId = this.project.manager?.id;
    if (!managerId) {
      return undefined;
    }
    return {
      name: RouteNames.MANAGER_ABOUT,
      params: { id: managerId },
    };
  }
  get isActionsShown(): boolean {
    return !this.isContractor;
  }

  get age(): number | string {
    if (!this.project.dob) {
      return "n/a";
    }

    const dob = new Date(this.project.dob);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();

    // Adjust age if the birth date hasn’t occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    return age;
  }

  get gender(): string {
    const gender = this.project.gender;
    if (gender)
      return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
  }
  get primaryContact(): string {
    return this.project.phone_number || "n/a";
  }
  get emailAddress(): string {
    return this.project.email_address || "n/a";
  }
  get emergencyName(): string | undefined {
    return this.project.emergency_contact_name;
  }
  get emergencyRelation(): string | undefined {
    return this.project.emergency_relationship;
  }
  get emergencyContact(): string | undefined {
    return this.project.emergency_phone_number;
  }
  get emergencyEmail(): string | undefined {
    return this.project.emergency_email;
  }
  get typeOfWork(): string {
    const items = this.project.support_type;
    return items?.join(", ") || "n/a";
  }
  get location(): string {
    const address = this.project.address || "n/a";
    return address && address.trim();
  }

  get googleMapsLink(): string {
    const baseMapsUrl = "https://www.google.com/maps/search/?api=1";
    const queryParam = `query=${encodeURIComponent(this.location)}`;
    return `${baseMapsUrl}&${queryParam}`;
  }

  refetchProject() {
    this.statusProcessing = false;
    // window.location.reload();
    this.$emit("updateProjectStatus", { completed: true, active: false , date_completed: new Date().toISOString()});
  }
  get contractorPageRoute() {
    return {
      name: RouteNames.CONTRACTOR_PROJECTS,
    };
  }

  get manager(): string {
    const firstName = this.project.manager.user?.given_name;
    const lastName = this.project.manager.user?.family_name;

    if (!firstName && !lastName) {
      return ``;
    }

    return `${firstName} ${lastName}`;
  }

  @Watch("projectCompleted")
  onProjectStatusChanged(): void {
    this.statusProcessing = false;
  }

  completeProject() {
    this.statusProcessing = true;
    this.dialog = true;

    // this.$confirm({
    //   title: `Do you really want to complete the project?`,
    //   message: `All tasks will be closed if you complete this project`,
    //   button: {
    //     no: "No",
    //     yes: "Yes",
    //   },
    //   callback: async (confirm: any) => {
    //     if (confirm) {
    //       this.$emit("updateProjectStatus", { completed: true, active: false });
    //       this.statusProcessing = true;
    //     }
    //   },
    // });
  }

  reopenProject(): void {
    this.$emit("updateProjectStatus", { completed: false, active: true });
    this.statusProcessing = true;
  }
}
